import type { OperatorSourceStatus, SourceConfig, VendorPage } from "@/lib/api";

export interface ScraperRowModel {
  key: string;
  vendorKey: string;
  vendorLabel: string;
  page: VendorPage | null;
  source: SourceConfig | null;
  status: OperatorSourceStatus | null;
}

export interface ScraperGroup {
  vendorKey: string;
  vendorLabel: string;
  rows: ScraperRowModel[];
}

export function buildScraperRows(
  vendorPages: VendorPage[],
  sources: SourceConfig[],
  statuses: OperatorSourceStatus[],
): ScraperRowModel[] {
  const sourcesByPage = new Map<string, SourceConfig[]>();
  const sourcesByUrl = new Map<string, SourceConfig[]>();
  const usedSourceIds = new Set<string>();
  for (const source of sources) {
    for (const pageId of sourceVendorPageIds(source)) {
      const bucket = sourcesByPage.get(pageId) ?? [];
      bucket.push(source);
      sourcesByPage.set(pageId, bucket);
    }
    if (source.source_url) {
      const bucket = sourcesByUrl.get(normalizeUrl(source.source_url)) ?? [];
      bucket.push(source);
      sourcesByUrl.set(normalizeUrl(source.source_url), bucket);
    }
  }
  const statusesBySourceId = new Map(statuses.map((status) => [status.source.id, status]));
  const rows: ScraperRowModel[] = [];

  for (const page of vendorPages) {
    const byPageId = sourcesByPage.get(page.id) ?? [];
    const byUrl = sourcesByUrl.get(normalizeUrl(page.url)) ?? [];
    const linkedSources = uniqueSources([...byPageId, ...byUrl]);
    if (linkedSources.length === 0) {
      if (page.status === "disabled") continue;
      rows.push({
        key: `page:${page.id}`,
        vendorKey: page.vendor,
        vendorLabel: page.vendor_display_name || page.vendor,
        page,
        source: null,
        status: null,
      });
      continue;
    }
    for (const source of linkedSources) {
      usedSourceIds.add(source.id);
      rows.push({
        key: `page-source:${page.id}:${source.id}`,
        vendorKey: page.vendor,
        vendorLabel: page.vendor_display_name || source.vendor || page.vendor,
        page,
        source,
        status: statusesBySourceId.get(source.id) ?? null,
      });
    }
  }

  for (const source of sources) {
    if (usedSourceIds.has(source.id)) continue;
    rows.push({
      key: `source:${source.id}`,
      vendorKey: source.vendor ?? "unmapped",
      vendorLabel: source.vendor ?? "Unmapped",
      page: null,
      source,
      status: statusesBySourceId.get(source.id) ?? null,
    });
  }

  return rows.sort((a, b) => {
    const vendorCompare = a.vendorLabel.localeCompare(b.vendorLabel);
    if (vendorCompare !== 0) return vendorCompare;
    const aLabel = a.page?.label ?? a.source?.display_name ?? a.key;
    const bLabel = b.page?.label ?? b.source?.display_name ?? b.key;
    return aLabel.localeCompare(bLabel);
  });
}

export function groupScraperRows(rows: ScraperRowModel[]): ScraperGroup[] {
  const groups = new Map<string, ScraperGroup>();
  for (const row of rows) {
    const group = groups.get(row.vendorKey) ?? {
      vendorKey: row.vendorKey,
      vendorLabel: row.vendorLabel,
      rows: [],
    };
    group.rows.push(row);
    groups.set(row.vendorKey, group);
  }
  return Array.from(groups.values());
}

function sourceVendorPageIds(source: SourceConfig): string[] {
  const config = source.config ?? {};
  const ids = new Set<string>();
  const single = config.vendor_page_id;
  if (typeof single === "string" && single.length > 0) ids.add(single);
  const multiple = config.vendor_page_ids;
  if (Array.isArray(multiple)) {
    for (const item of multiple) {
      if (typeof item === "string" && item.length > 0) ids.add(item);
    }
  }
  return Array.from(ids);
}

function uniqueSources(sources: SourceConfig[]): SourceConfig[] {
  const seen = new Set<string>();
  const unique: SourceConfig[] = [];
  for (const source of sources) {
    if (seen.has(source.id)) continue;
    seen.add(source.id);
    unique.push(source);
  }
  return unique;
}

function normalizeUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}
